import { useState, useEffect, useRef } from "react";
import { Modal, Box, Button, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import Quill from "quill";
import { marked } from "marked";
import "react-quill/dist/quill.snow.css";
import "./addFaqModal.css";

const Delta = Quill.import("delta");

const ANSWER_MAX_CHARS = 2000;

function cleanHtmlForQuill(html) {
  let s = (html || "").trim();
  s = s.replace(/(?:\s*<p><br><\/p>\s*)+$/gi, "");
  s = s.replace(/<\/p>\s*(?:<p><br><\/p>\s*)+(?=<\s*(ul|ol)\b)/gi, "</p>");
  s = s.replace(/^\s*<p><br><\/p>\s*/i, "");
  return s;
}

function paragraphMatcher(node, delta) {
  const isEmptyPara =
    node.tagName === "P" &&
    node.childNodes.length === 1 &&
    node.firstChild &&
    node.firstChild.nodeName === "BR";

  if (!isEmptyPara) return delta;

  const prev = node.previousElementSibling;
  const next = node.nextElementSibling;
  const prevIsParagraph = prev && prev.tagName === "P";
  const nextIsList = next && (next.tagName === "UL" || next.tagName === "OL");

  if (prevIsParagraph && nextIsList) {
    return new Delta();
  }
  return delta;
}

function htmlToPlainLength(html) {
  if (!html) return 0;
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = (div.textContent || div.innerText || "").replace(/\n$/, "");
  return text.length;
}

const EditFaqModal = ({ open, onClose, onEditFaq, faqItem, submitting = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [title, setTitle] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [initialHtml, setInitialHtml] = useState("");
  const [editorKey, setEditorKey] = useState(0);
  const [answerCharsUsed, setAnswerCharsUsed] = useState(0);

  const quillRef = useRef(null);

  useEffect(() => {
    if (open && faqItem) {
      setTitle(faqItem.title || "");
      setQuestion(faqItem.question || "");
      const converted = faqItem.answer ? marked.parse(faqItem.answer) : "";
      const sanitized = cleanHtmlForQuill(converted);
      setInitialHtml(sanitized);
      setAnswer(sanitized);
      setAnswerCharsUsed(htmlToPlainLength(sanitized));
      setEditorKey((k) => k + 1);
    }
  }, [open, faqItem]);

  const canSave = title.trim() && question.trim() && answer.trim();

  const handleSave = () => {
    if (!canSave || submitting) return;
    onEditFaq({ knowledgeId: faqItem.id, title: title.trim(), question: question.trim(), answer: answer.trim() });
  };

  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"]
    ],
    clipboard: {
      matchVisual: false,
      matchers: [["P", paragraphMatcher]]
    }
  };

  const handleEditorChange = (html, delta, source, editor) => {
    const quill = quillRef.current ? quillRef.current.getEditor() : null;
    let visibleLen = editor.getLength() - 1;

    if (source === "user" && quill && visibleLen > ANSWER_MAX_CHARS) {
      quill.deleteText(ANSWER_MAX_CHARS, visibleLen - ANSWER_MAX_CHARS);
      visibleLen = ANSWER_MAX_CHARS;

      const currentHtml = cleanHtmlForQuill(quill.root.innerHTML);
      setAnswer(currentHtml);
      setAnswerCharsUsed(visibleLen);
      return;
    }

    setAnswer(cleanHtmlForQuill(html));
    setAnswerCharsUsed(Math.max(0, visibleLen));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "white",
          borderRadius: "10px",
          boxShadow: 24,
          p: isMobile ? 2.5 : 3.5,
          paddingTop: 2.5,
          width: isMobile ? "80%" : "94%",
          maxWidth: 700,
          maxHeight: "88%",
          overflow: "auto",
          outline: "none"
        }}
      >
        <h2 className="afq-title">{t("edit_qa")}</h2>

        <div className="afq-row">
          <TextField
            fullWidth
            label={t("faq_title_label")}
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
              "& .MuiOutlinedInput-root.Mui-focused fieldset": { border: "1px solid #515151" },
              "& .MuiInputLabel-root": { color: "gray", "&.Mui-focused": { color: "#5616ea" } }
            }}
          />
        </div>

        <div className="afq-row">
          <TextField
            fullWidth
            label={t("faq_question_label")}
            variant="outlined"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
              "& .MuiOutlinedInput-root.Mui-focused fieldset": { border: "1px solid #515151" },
              "& .MuiInputLabel-root": { color: "gray", "&.Mui-focused": { color: "#5616ea" } }
            }}
          />
        </div>

        <div className="afq-row">
          <label className="faq-editor-label">{t("faq_answer_label")}</label>

          <ReactQuill
            ref={quillRef}
            key={editorKey}
            defaultValue={initialHtml}
            onChange={handleEditorChange}
            modules={quillModules}
            formats={["bold", "italic", "underline", "list", "bullet", "link"]}
            className="faq-editor"
          />

          <div className={`faq-char-counter ${answerCharsUsed >= ANSWER_MAX_CHARS ? "limit" : ""}`}>
            {answerCharsUsed}/{ANSWER_MAX_CHARS}
          </div>
        </div>

        <div className="afq-actions">
          <Button
            onClick={onClose}
            variant="outlined"
            className="afq-cancel-btn"
            sx={{
              color: "#5616ea",
              borderColor: "#5616ea",
              "&:hover": { borderColor: "#5e0fb3", backgroundColor: "#f3eaff" }
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            className="afq-primary-btn"
            disabled={!canSave || submitting}
            sx={{
              backgroundColor: "#5616ea",
              "&:hover": { backgroundColor: "#4a14d6" },
              "&.Mui-disabled": { backgroundColor: "#cfc7ff" }
            }}
          >
            {t("save")}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default EditFaqModal;
